import type { SxProps, Theme } from '@mui/material'
import { resumePulseSx } from '@/features/status/constants'

// Width of the revealed left column. Sized to hug the conversation button
// (icon ~24px at 1.5rem + button padding 0.25 ≈ 28px) with ~1px slack so the
// column reads as proportional to the glyph rather than an oversized pane.
export const CONVERSATION_COLUMN_WIDTH = 30

// The reveal column carries the spacing between itself and the detail content as
// its own right margin (instead of the container gap) so that the gap collapses
// together with the width when the column is hidden, leaving the content flush
// to the card's left edge.
export const CONVERSATION_COLUMN_GAP = '12px'
export const CONVERSATION_REVEAL_CLASS = 'conversation-reveal'

// Shared base so the idle and transitioning containers stay in sync. No `gap`:
// spacing lives on the reveal column's margin so it collapses with the width.
export const baseContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  [`&:hover .${CONVERSATION_REVEAL_CLASS}`]: {
    width: `${CONVERSATION_COLUMN_WIDTH}px`,
    marginRight: CONVERSATION_COLUMN_GAP,
    opacity: 1,
    // Release the clip once revealed so the button's scale(1.35) hover is not
    // cropped by the narrow column (scaled button ~37.8px > 30px column; it
    // overflows into the card padding / gap). Restore tab-reachability too.
    overflow: 'visible',
    visibility: 'visible',
  },
}

export const orchestratorContainerSx: SxProps<Theme> = {
  ...baseContainerSx,
}

export const transitioningContainerSx: SxProps<Theme> = {
  ...baseContainerSx,
  ...resumePulseSx,
}

export const conversationColumnSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  // Collapsed default: clip the slide-in wipe and keep the hidden button out of
  // the keyboard tab order. Both release at the revealed states below.
  overflow: 'hidden',
  width: 0,
  marginRight: 0,
  opacity: 0,
  visibility: 'hidden',
  transition: 'width 200ms ease, margin 200ms ease, opacity 200ms ease',
  // Touch devices have no hover — always show the button there.
  '@media (hover: none)': {
    width: `${CONVERSATION_COLUMN_WIDTH}px`,
    marginRight: CONVERSATION_COLUMN_GAP,
    opacity: 1,
    overflow: 'visible',
    visibility: 'visible',
  },
}

export const detailColumnSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  minWidth: 0,
  flex: 1,
}

export const detailActivityRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
}

export const conversationButtonSx: SxProps<Theme> = {
  padding: 0.25,
  color: 'primary.main',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'transparent',
    transform: 'scale(1.35)',
  },
}

export const conversationButtonDisabledSx: SxProps<Theme> = {
  padding: 0.25,
  color: 'text.disabled',
  opacity: 0.4,
  cursor: 'default',
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '&.Mui-disabled': {
    color: 'text.disabled',
    opacity: 0.4,
  },
}

export const conversationIconSx: SxProps<Theme> = {
  fontSize: '1.5rem',
}

export const transientPreviewRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  cursor: 'pointer',
}

export const transientMessageSx: SxProps<Theme> = {
  fontWeight: 400,
  fontSize: '0.875rem',
  color: 'text.primary',
  lineHeight: 1.4,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: 'color 0.2s',
  '&:hover': {
    color: 'primary.main',
  },
}

export const replyIconButtonSx: SxProps<Theme> = {
  padding: 0.25,
  color: 'success.main',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'transparent',
    transform: 'scale(1.35)',
  },
}

export const replyIconSizeSx: SxProps<Theme> = {
  fontSize: '1.1rem',
}
